import React,{useState, useEffect} from "react"
import styles from "./SupportPage.module.scss"
import classNames from "classnames/bind"
import { useLocation, useNavigate } from 'react-router-dom'
import useTranslations from "../../hooks/useTranslations"
import { useLanguageStore } from "../../stores/Language/useLanguageStore"
import { usePageSections } from "../../hooks/usePageSections"
import SectionRenderer from "../../components/SectionRenderer/SectionRenderer"

const cx = classNames.bind(styles)

const SupportPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const currentTranslations = useTranslations()
  const currentLanguage = useLanguageStore((state) => state.currentLanguage)
  const { sections } = usePageSections("support")
  
  const [expandedSections, setExpandedSections] = useState({
    warranty: false,
    return: false,
    shipping: false,
    privacy: false,
    payment: false
  })

  // Fallback content for support page (nội dung dự phòng)
  const supportContent = currentTranslations?.SupportPage || {
    title: currentTranslations?.support?.title || "CHÍNH SÁCH VÀ ĐIỀU KHOẢN MUA HÀNG",
    subtitle: "POLICIES & TERMS OF PURCHASE",
    intro_text: {
      line1: "Tại Lianne Jewelry, chúng tôi cam kết mang đến cho khách hàng không chỉ những sản phẩm trang sức tinh xảo mà còn trải nghiệm mua sắm minh bạch và an toàn.",
      line1_en: "At Lianne Jewelry, we are committed to providing customers not only exquisite jewelry products but also transparent and secure shopping experience.",
      line2: "Khi mua sắm tại website hoặc cửa hàng chính thức, khách hàng đồng nghĩa với việc chấp nhận các điều khoản liên quan đến quy trình đặt hàng, thanh toán, giao nhận, đổi trả, bảo hành và bảo mật thông tin.",
      line2_en: "When shopping at the website or official store, customers agree to terms regarding ordering process, payment, delivery, returns, warranty and information security.",
      line3: "Chúng tôi tin rằng mỗi món trang sức không chỉ mang giá trị thẩm mỹ mà còn chứa đựng cảm xúc và kỷ niệm riêng.",
      line3_en: "We believe that each piece of jewelry carries not only aesthetic value but also personal emotions and memories."
    },
    expand_all: "Mở rộng tất cả",
    expand_all_en: "Expand All",
    collapse_all: "Thu gọn tất cả",
    collapse_all_en: "Collapse All",
    
    // Warranty Policy
    warranty: {
      title: "1. Chính sách bảo hành",
      title_en: "1. Warranty Policy",
      intro: "Tại Lianne Jewelry, chúng tôi cam kết mang đến cho khách hàng những sản phẩm trang sức chất lượng và dịch vụ hậu mãi uy tín. Mỗi sản phẩm khi mua tại cửa hàng hoặc website đều được bảo hành theo các điều kiện sau:",
      intro_en: "At Lianne Jewelry, we are committed to providing customers with quality jewelry products and reliable after-sales service. Every product purchased at the store or website comes with warranty under the following conditions:",
      items: [
        {
          title: "Thời gian bảo hành:",
          title_en: "Warranty Period:",
          content: "12 tháng đối với trang sức Bạc kể từ ngày mua hàng.",
          content_en: "12 months for Silver jewelry from the date of purchase."
        },
        {
          title: "Phạm vi bảo hành:",
          title_en: "Warranty Coverage:",
          content: "Áp dụng cho các lỗi kỹ thuật từ nhà sản xuất như: bong tróc xi mạ, rơi đá do lỗi gắn kết, gãy móc cài.",
          content_en: "Applied to manufacturing defects such as: plating peeling, stone loss due to setting defects, clasp breakage."
        },
        {
          title: "Không áp dụng bảo hành:",
          title_en: "Warranty Exclusions:",
          content: "Sản phẩm bị mất viên chủ, trầy xước, biến dạng do va đập mạnh, tiếp xúc với hóa chất hoặc sử dụng sai hướng dẫn.",
          content_en: "Products with lost gemstones, scratches, deformation from impact, chemical contact or improper use."
        },
        {
          title: "Quy trình bảo hành:",
          title_en: "Warranty Process:",
          content: "Khách hàng liên hệ trực tiếp qua hotline hoặc fanpage, cung cấp hóa đơn mua hàng. Lianne Jewelry sẽ tiếp nhận và hỗ trợ xử lý trong thời gian sớm nhất.",
          content_en: "Customers contact directly via hotline or fanpage, provide purchase invoice. Lianne Jewelry will receive and process support as soon as possible."
        }
      ]
    },
    
    // Return & Refund Policy
    return: {
      title: "2. Chính sách đổi trả & hoàn tiền",
      title_en: "2. Return & Refund Policy",
      intro: "Để mang lại trải nghiệm mua sắm an tâm, Lianne Jewelry áp dụng chính sách đổi trả minh bạch:",
      intro_en: "To provide a worry-free shopping experience, Lianne Jewelry applies a transparent return policy:",
      items: [
        {
          title: "Thời gian đổi trả:",
          title_en: "Return Period:",
          content: "Trong vòng 7 ngày kể từ ngày nhận sản phẩm.",
          content_en: "Within 7 days from the date of product receipt."
        },
        {
          title: "Điều kiện đổi trả:",
          title_en: "Return Conditions:",
          content: "Sản phẩm còn nguyên vẹn, chưa qua sử dụng, đầy đủ hộp đựng, hóa đơn và tem mác.",
          content_en: "Product must be intact, unused, with complete packaging, invoice and tags."
        },
        {
          title: "Trường hợp được đổi trả/hoàn tiền:",
          title_en: "Eligible for Return/Refund:",
          content: "Sản phẩm lỗi từ nhà sản xuất. Giao nhầm mẫu, sai kích thước, sai số lượng.",
          content_en: "Manufacturing defects. Wrong model, size, or quantity delivered."
        },
        {
          title: "Chi phí vận chuyển:",
          title_en: "Shipping Costs:",
          content: "Nếu lỗi từ phía Lianne Jewelry, chúng tôi sẽ chịu toàn bộ chi phí đổi trả.",
          content_en: "If the error is from Lianne Jewelry, we will bear all return shipping costs."
        }
      ]
    },
    
    // Shipping & Delivery Policy
    shipping: {
      title: "3. Chính sách vận chuyển & giao hàng",
      title_en: "3. Shipping & Delivery Policy",
      intro: "Lianne Jewelry cam kết giao hàng nhanh chóng, an toàn đến tay khách hàng:",
      intro_en: "Lianne Jewelry is committed to fast and safe delivery to customers:",
      items: [
        {
          title: "Phạm vi giao hàng:",
          title_en: "Delivery Area:",
          content: "Toàn quốc và quốc tế.",
          content_en: "Nationwide and international."
        },
        {
          title: "Đơn vị vận chuyển:",
          title_en: "Shipping Partners:",
          content: "Hợp tác với các đơn vị uy tín như GHTK, GHN và J&T",
          content_en: "Partnership with reputable carriers like GHTK, GHN and J&T"
        },
        {
          title: "Thời gian giao hàng:",
          title_en: "Delivery Time:",
          content: "Nội thành: 1 ngày làm việc. Ngoại tỉnh: 2–3 ngày làm việc. Quốc tế: 7–15 ngày tùy khu vực.",
          content_en: "Inner city: 1 business day. Provincial: 2–3 business days. International: 7–15 days depending on region."
        },
        {
          title: "Phí vận chuyển:",
          title_en: "Shipping Fees:",
          content: "Miễn phí cho đơn hàng từ 2 sản phẩm trở lên trong nước. Quốc tế tính theo bảng phí từng khu vực.",
          content_en: "Free shipping for domestic orders of 2+ products. International rates vary by region."
        }
      ],
      note: "*Lưu ý: Khách hàng vui lòng kiểm tra sản phẩm trước khi ký nhận.",
      note_en: "*Note: Please check products before signing for receipt."
    },
    
    // Privacy Policy  
    privacy: {
      title: "4. Chính sách bảo mật thông tin khách hàng",
      title_en: "4. Customer Data Privacy Policy",
      intro: "Bảo mật thông tin khách hàng là ưu tiên hàng đầu của Lianne Jewelry:",
      intro_en: "Customer information security is Lianne Jewelry's top priority:",
      items: [
        {
          title: "Thông tin thu thập:",
          title_en: "Information Collected:",
          content: "Họ tên, số điện thoại, email, địa chỉ nhận hàng.",
          content_en: "Full name, phone number, email, delivery address."
        },
        {
          title: "Mục đích sử dụng:",
          title_en: "Usage Purpose:",
          content: "Xử lý đơn hàng, chăm sóc khách hàng, gửi ưu đãi và chương trình khuyến mãi.",
          content_en: "Order processing, customer service, sending offers and promotional programs."
        },
        {
          title: "Bảo mật tuyệt đối:",
          title_en: "Absolute Security:",
          content: "Chúng tôi cam kết không bán, trao đổi hoặc tiết lộ thông tin cá nhân cho bên thứ ba, trừ trường hợp bắt buộc theo pháp luật.",
          content_en: "We commit not to sell, exchange or disclose personal information to third parties, except as required by law."
        },
        {
          title: "Quyền của khách hàng:",
          title_en: "Customer Rights:",
          content: "Có thể yêu cầu chỉnh sửa hoặc xóa thông tin bất cứ lúc nào qua email/hotline.",
          content_en: "Can request modification or deletion of information anytime via email/hotline."
        }
      ]
    },
    
    // Payment Policy
    payment: {
      title: "5. Chính sách thanh toán",
      title_en: "5. Payment Policy", 
      intro: "Để thuận tiện, Lianne Jewelry hỗ trợ nhiều hình thức thanh toán:",
      intro_en: "For convenience, Lianne Jewelry supports multiple payment methods:",
      items: [
        {
          content: "Thanh toán khi nhận hàng (COD).",
          content_en: "Cash on Delivery (COD)."
        },
        {
          content: "Chuyển khoản ngân hàng.",
          content_en: "Bank transfer."
        },
        {
          content: "Thanh toán qua thẻ Visa/MasterCard, ví điện tử (Momo, ZaloPay…).",
          content_en: "Payment via Visa/MasterCard, e-wallets (Momo, ZaloPay...)."
        },
        {
          title: "Đơn quốc tế:",
          title_en: "International Orders:",
          content: "Bắt buộc thanh toán trước qua chuyển khoản hoặc thẻ quốc tế.",
          content_en: "Pre-payment required via bank transfer or international card."
        }
      ],
      commitment: "Cam kết: Tất cả giao dịch trực tuyến đều được mã hóa và bảo mật theo tiêu chuẩn quốc tế.",
      commitment_en: "Commitment: All online transactions are encrypted and secured to international standards."
    }
  }

  // Đọc URL parameters và tự động mở section tương ứng
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const section = searchParams.get('section')
    
    if (section && expandedSections.hasOwnProperty(section)) {
      setExpandedSections(prev => ({
        ...prev,
        [section]: true
      }))
      
      // Scroll đến section sau khi component đã render
      setTimeout(() => {
        const element = document.getElementById(`section-${section}`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [location.search])

  if (sections?.length) {
    return (
      <div>
        {sections.map((section) => (
          <SectionRenderer key={section.id} section={section} />
        ))}
      </div>
    )
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
    
    // Cập nhật URL khi mở/đóng section
    const searchParams = new URLSearchParams(location.search)
    if (expandedSections[section]) {
      // Nếu đang mở thì xóa parameter
      searchParams.delete('section')
    } else {
      // Nếu đang đóng thì thêm parameter
      searchParams.set('section', section)
    }
    
    const newUrl = searchParams.toString() ? 
      `${location.pathname}?${searchParams.toString()}` : 
      location.pathname
    
    navigate(newUrl, { replace: true })
  }

  const expandAll = () => {
    const allExpanded = Object.values(expandedSections).every(val => val === true)
    const newState = {}
    Object.keys(expandedSections).forEach(key => {
      newState[key] = !allExpanded
    })
    setExpandedSections(newState)
  }

  return (
    <div className={cx('support-container')}>
      <div className={cx('container')}>
        {/* Header Section */}
        <div className={cx('header-section')}>
          <h1 className={cx('main-title')}>
            {currentLanguage === 'vi' ? supportContent.title : supportContent.subtitle}
          </h1>
          <div className={cx('intro-text')}>
            <p>
              {currentLanguage === 'vi' 
                ? supportContent.intro_text.line1 
                : supportContent.intro_text.line1_en}
            </p>
            <p>
              {currentLanguage === 'vi' 
                ? supportContent.intro_text.line2 
                : supportContent.intro_text.line2_en}
            </p>
            <p>
              {currentLanguage === 'vi' 
                ? supportContent.intro_text.line3 
                : supportContent.intro_text.line3_en}
            </p>
          </div>
          <div className={cx('expand-all')} onClick={expandAll}>
            {Object.values(expandedSections).every(val => val === true) 
              ? (currentLanguage === 'vi' 
                  ? supportContent.collapse_all 
                  : supportContent.collapse_all_en)
              : (currentLanguage === 'vi' 
                  ? supportContent.expand_all 
                  : supportContent.expand_all_en)}
          </div>
        </div>

        {/* Policy Sections */}
        <div className={cx('policy-sections')}>
          {/* Warranty Policy */}
          <div id="section-warranty" className={cx('policy-item', { 'expanded': expandedSections.warranty, 'collapsed': !expandedSections.warranty })}>
            <div className={cx('policy-header')} onClick={() => toggleSection('warranty')}>
              <h2>{currentLanguage === 'vi' ? supportContent.warranty.title : supportContent.warranty.title_en}</h2>
              <span className={cx('toggle-icon', { 'expanded': expandedSections.warranty })}>↓</span>
            </div>
            {expandedSections.warranty && (
              <div className={cx('policy-content')}>
                <p>
                  {currentLanguage === 'vi' ? supportContent.warranty.intro : supportContent.warranty.intro_en}
                </p>
                <ul>
                  {supportContent.warranty.items.map((item, index) => (
                    <li key={index}>
                      <strong>
                        {currentLanguage === 'vi' ? item.title : item.title_en}
                      </strong>{' '}
                      {currentLanguage === 'vi' ? item.content : item.content_en}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Return & Refund Policy */}
          <div id="section-return" className={cx('policy-item', { 'expanded': expandedSections.return, 'collapsed': !expandedSections.return })}>
            <div className={cx('policy-header')} onClick={() => toggleSection('return')}>
              <h2>{currentLanguage === 'vi' ? supportContent.return.title : supportContent.return.title_en}</h2>
              <span className={cx('toggle-icon', { 'expanded': expandedSections.return })}>↓</span>
            </div>
            {expandedSections.return && (
              <div className={cx('policy-content')}>
                <p>{currentLanguage === 'vi' ? supportContent.return.intro : supportContent.return.intro_en}</p>
                <ul>
                  {supportContent.return.items.map((item, index) => (
                    <li key={index}>
                      <strong>
                        {currentLanguage === 'vi' ? item.title : item.title_en}
                      </strong>{' '}
                      {currentLanguage === 'vi' ? item.content : item.content_en}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Shipping Policy */}
          <div id="section-shipping" className={cx('policy-item', { 'expanded': expandedSections.shipping, 'collapsed': !expandedSections.shipping })}>
            <div className={cx('policy-header')} onClick={() => toggleSection('shipping')}>
              <h2>{currentLanguage === 'vi' ? supportContent.shipping.title : supportContent.shipping.title_en}</h2>
              <span className={cx('toggle-icon', { 'expanded': expandedSections.shipping })}>↓</span>
            </div>
            {expandedSections.shipping && (
              <div className={cx('policy-content')}>
                <p>{currentLanguage === 'vi' ? supportContent.shipping.intro : supportContent.shipping.intro_en}</p>
                <ul>
                  {supportContent.shipping.items.map((item, index) => (
                    <li key={index}>
                      <strong>
                        {currentLanguage === 'vi' ? item.title : item.title_en}
                      </strong>{' '}
                      {currentLanguage === 'vi' ? item.content : item.content_en}
                    </li>
                  ))}
                </ul>
                <p><em>{currentLanguage === 'vi' ? supportContent.shipping.note : supportContent.shipping.note_en}</em></p>
              </div>
            )}
          </div>

          {/* Privacy Policy */}
          <div id="section-privacy" className={cx('policy-item', { 'expanded': expandedSections.privacy, 'collapsed': !expandedSections.privacy })}>
            <div className={cx('policy-header')} onClick={() => toggleSection('privacy')}>
              <h2>{currentLanguage === 'vi' ? supportContent.privacy.title : supportContent.privacy.title_en}</h2>
              <span className={cx('toggle-icon', { 'expanded': expandedSections.privacy })}>↓</span>
            </div>
            {expandedSections.privacy && (
              <div className={cx('policy-content')}>
                <p>{currentLanguage === 'vi' ? supportContent.privacy.intro : supportContent.privacy.intro_en}</p>
                <ul>
                  {supportContent.privacy.items.map((item, index) => (
                    <li key={index}>
                      <strong>
                        {currentLanguage === 'vi' ? item.title : item.title_en}
                      </strong>{' '}
                      {currentLanguage === 'vi' ? item.content : item.content_en}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Payment Policy */}
          <div id="section-payment" className={cx('policy-item', { 'expanded': expandedSections.payment, 'collapsed': !expandedSections.payment })}>
            <div className={cx('policy-header')} onClick={() => toggleSection('payment')}>
              <h2>{currentLanguage === 'vi' ? supportContent.payment.title : supportContent.payment.title_en}</h2>
              <span className={cx('toggle-icon', { 'expanded': expandedSections.payment })}>↓</span>
            </div>
            {expandedSections.payment && (
              <div className={cx('policy-content')}>
                <p>{currentLanguage === 'vi' ? supportContent.payment.intro : supportContent.payment.intro_en}</p>
                <ul>
                  {supportContent.payment.items.map((item, index) => (
                    <li key={index}>
                      {item.title && (
                        <strong>
                          {currentLanguage === 'vi' ? item.title : item.title_en}
                        </strong>
                      )}
                      {' '}
                      {currentLanguage === 'vi' ? item.content : item.content_en}
                    </li>
                  ))}
                </ul>
                <p><strong>{currentLanguage === 'vi' ? supportContent.payment.commitment : supportContent.payment.commitment_en}</strong></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportPage;
